import React, { useEffect, useRef } from 'react';
import { useEditor, EditorContent, BubbleMenu } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import TextStyle from '@tiptap/extension-text-style';
import FontFamily from '@tiptap/extension-font-family';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import TextAlign from '@tiptap/extension-text-align';
import { cn } from '@/lib/utils';
import EditorToolbar from '@/components/settings/rich-text-editor/EditorToolbar';
import FontSize from '@/components/settings/rich-text-editor/FontSizeExtension';
import CustomImage from '@/components/settings/rich-text-editor/ImageExtension';
import ImageToolbar from '@/components/settings/rich-text-editor/ImageToolbar';
import DynamicFieldSelector from './DynamicFieldSelector';

interface RichTextEditorWithDynamicFieldsProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  minHeight?: string;
}

const RichTextEditorWithDynamicFields: React.FC<RichTextEditorWithDynamicFieldsProps> = ({
  value,
  onChange,
  placeholder = 'Start typing...',
  disabled = false,
  className,
  minHeight = '150px',
}) => {
  const lastExternalValue = useRef<string | undefined>(undefined);
  const hasUserEdited = useRef(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        horizontalRule: false,
        codeBlock: false,
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-primary underline cursor-pointer',
        },
      }),
      Underline,
      TextStyle,
      FontFamily,
      FontSize,
      Color,
      Highlight.configure({ multicolor: true }),
      CustomImage.configure({
        inline: true,
        allowBase64: true,
        HTMLAttributes: {
          class: 'h-auto rounded cursor-pointer transition-all',
        },
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
    ] as any,
    content: '',
    editable: !disabled,
    onUpdate: ({ editor }) => {
      hasUserEdited.current = true;
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: `prose prose-sm max-w-none focus:outline-none px-3 py-2`,
        style: `min-height: ${minHeight}`,
      },
    },
  });

  // Sync external value to editor
  useEffect(() => {
    if (!editor) return;
    
    if (value !== lastExternalValue.current) {
      const currentContent = editor.getHTML();
      const isEditorEmpty = currentContent === '<p></p>' || currentContent === '';
      
      if (isEditorEmpty || !hasUserEdited.current || !value) {
        editor.commands.setContent(value || '');
        hasUserEdited.current = false;
      }
      
      lastExternalValue.current = value;
    }
  }, [value, editor]);

  // Reset hasUserEdited when value is cleared (form reset)
  useEffect(() => {
    if (value === '' || value === '<p></p>') {
      hasUserEdited.current = false;
    }
  }, [value]);

  const handleFieldSelect = (field: string) => {
    if (editor) {
      editor.chain().focus().insertContent(field).run();
    }
  };

  if (!editor) {
    return null;
  }

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex justify-end">
        <DynamicFieldSelector 
          onFieldSelect={handleFieldSelect} 
          disabled={disabled}
        />
      </div>
      <div className={cn('border rounded-md bg-background', disabled && 'opacity-50')}>
        {/* Toolbar */}
        <EditorToolbar editor={editor} disabled={disabled} />

        {/* Image Bubble Menu */}
        <BubbleMenu
          editor={editor}
          tippyOptions={{ 
            duration: 100,
            placement: 'top',
          }}
          shouldShow={({ state }) => {
            const { selection } = state;
            const node = state.doc.nodeAt(selection.from);
            return node?.type.name === 'image';
          }}
        >
          <ImageToolbar editor={editor} />
        </BubbleMenu>

        {/* Editor Content */}
        <EditorContent 
          editor={editor} 
          className={cn(
            disabled && 'pointer-events-none',
          )}
        />

        {/* CSS for float, layering, and selection styling */}
        <style>{`
          .ProseMirror img.ProseMirror-selectednode {
            outline: 2px solid hsl(var(--primary));
            outline-offset: 2px;
          }
          .ProseMirror img[style*="float: left"] {
            float: left;
          }
          .ProseMirror img[style*="float: right"] {
            float: right;
          }
          .ProseMirror img[style*="display: block"] {
            display: block;
          }
          .ProseMirror p:empty::before {
            content: "\\00a0";
          }
          .ProseMirror::after {
            content: "";
            display: table;
            clear: both;
          }
        `}</style>
      </div>
    </div>
  );
};

export default RichTextEditorWithDynamicFields;
